import {
	AnalyticsRoutes,
	App,
	getEnvironmentFromUrl,
	MyRoutes,
	RedirectService,
	VehicleDataAge,
} from '@agilox/common';
import { RemovableFilterButtonComponent, TooltipModule } from '@agilox/ui';
import { Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, switchMap } from 'rxjs';
import { STALE_HOURS_BY_LEVEL } from './constants/stale-hours-by-level.const';
import { UP_TO_DATE_VEHICLE_DATA } from './constants/up-to-date-vehicle-data.const';
import { VEHICLE_DATA_PILL_TRANSLATION_PREFIX } from './constants/vehicle-data-pill-translation-prefix.const';
import { VehicleDataType } from './enums/vehicle-data-type.enum';
import { VehicleDataActualityTooltipPipe } from './pipes/vehicle-data-actuality-tooltip.pipe';
import { VehicleDataStatusService } from './services/vehicle-data-status.service';
import { technicalDataDeepLink } from './utils/technical-data-deep-link.util';
import { sameVehicleDataQuery, toQuerySerials } from './utils/vehicle-data-actuality.util';

const HOST_APP_ROUTES = [MyRoutes, AnalyticsRoutes];

const STALE_LABEL_KEY = `${VEHICLE_DATA_PILL_TRANSLATION_PREFIX}.stale`;
const MISSING_LABEL_KEY = `${VEHICLE_DATA_PILL_TRANSLATION_PREFIX}.missing`;
const UP_TO_DATE_LABEL_KEY = `${VEHICLE_DATA_PILL_TRANSLATION_PREFIX}.up_to_date`;
const LESS_THAN_HOUR_LABEL_KEY = `${VEHICLE_DATA_PILL_TRANSLATION_PREFIX}.less_than_hour`;

const DOT_COLOR_BY_LEVEL: Record<VehicleDataAge, string> = {
	[VehicleDataAge.UP_TO_DATE]: 'bg-success',
	[VehicleDataAge.OUTDATED]: 'bg-warning',
	[VehicleDataAge.CRITICAL]: 'bg-error',
	[VehicleDataAge.MISSING]: 'bg-error',
};

@Component({
	selector: 'vehicle-data-pill',
	templateUrl: './vehicle-data-pill.component.html',
	host: { '[style.display]': "actuality().totalVehicles ? null : 'none'" },
	imports: [RemovableFilterButtonComponent, TooltipModule, VehicleDataActualityTooltipPipe],
})
export class VehicleDataPillComponent {
	private vehicleDataStatusService = inject(VehicleDataStatusService);
	private redirectService = inject(RedirectService);
	private translate = inject(TranslateService);

	relevantDataTypes = input.required<VehicleDataType[]>();
	serials = input<string[]>([]);
	singleVehicle = input(false);

	private querySerials = computed(() => toQuerySerials(this.serials()));

	private query = computed(() => ({
		serials: this.querySerials(),
		dataTypes: this.relevantDataTypes(),
	}));

	dotColor = computed(() => DOT_COLOR_BY_LEVEL[this.actuality().level]);

	label = computed(() => {
		const { level, oldestAgeInHours } = this.actuality();

		if (level === VehicleDataAge.MISSING) {
			return this.translate.instant(MISSING_LABEL_KEY);
		}
		if (level !== VehicleDataAge.UP_TO_DATE) {
			return this.translate.instant(STALE_LABEL_KEY, { hours: STALE_HOURS_BY_LEVEL[level] });
		}

		return oldestAgeInHours
			? this.translate.instant(UP_TO_DATE_LABEL_KEY, { hours: oldestAgeInHours })
			: this.translate.instant(LESS_THAN_HOUR_LABEL_KEY);
	});

	technicalDataUrl = computed(() => {
		const appUrl = this.redirectService.getRedirectAppUrl(
			App.my,
			getEnvironmentFromUrl(HOST_APP_ROUTES)
		);

		return `${appUrl}/#${technicalDataDeepLink(this.querySerials())}`;
	});

	actuality = toSignal(
		toObservable(this.query).pipe(
			distinctUntilChanged(sameVehicleDataQuery),
			switchMap(({ serials, dataTypes }) =>
				this.vehicleDataStatusService.getActuality(serials, dataTypes)
			)
		),
		{ initialValue: UP_TO_DATE_VEHICLE_DATA }
	);
}
